import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactTimeAgo from "react-time-ago";
import { IoPersonCircleOutline as AuthorIcon, IoCopyOutline, IoCheckmarkOutline } from "react-icons/io5";
import ReactMarkdown from "react-markdown";
import { Loader, Error } from "../../components";
import "github-markdown-css/github-markdown-light.css";
import styles from "./ArticleDetail.module.css";

const parseFrontmatter = (mdText) => {
  const match = mdText.match(/^---\n([\s\S]*?)\n---/);
  if (!match) return { data: {}, body: mdText };

  const body = mdText.slice(match[0].length).trim();
  const data = {};
  match[1].split("\n").forEach((line) => {
    const colonIdx = line.indexOf(":");
    if (colonIdx === -1) return;
    const key = line.slice(0, colonIdx).trim();
    const val = line.slice(colonIdx + 1).trim().replace(/^["']|["']$/g, "");
    data[key] = val;
  });

  return { data, body };
};

const getBase = () =>
  process.env.REACT_APP_TEST_MODE === "true"
    ? ""
    : process.env.REACT_APP_S3_BASE_URL || "";

export const ArticleDetail = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [body, setBody] = useState("");
  const [rawMd, setRawMd] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const base = getBase();
        const res = await fetch(`${base}/content/articles/${slug}.md`);
        if (!res.ok) throw new Error("Article not found.");
        const mdText = await res.text();
        const { data, body: mdBody } = parseFrontmatter(mdText);
        setRawMd(mdText);
        setArticle(data);
        setBody(mdBody);
        setError("");
      } catch (err) {
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rawMd);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* silent fail */ }
  };

  if (loading) return <Loader />;
  if (error) return <Error errorMessage={error} />;

  return (
    <div className={styles.detail_page}>
      <Link to="/" className={styles.back_link}>← Back to Home</Link>

      <div className={styles.toolbar}>
        <button className={styles.copy_btn} onClick={handleCopy} title="Copy article markdown">
          {copied ? <IoCheckmarkOutline /> : <IoCopyOutline />}
          <span>{copied ? "Copied" : "Copy .md"}</span>
        </button>
      </div>

      <article className={styles.article}>
        <p className={styles.section}>{article.section?.toUpperCase()}</p>
        <h1 className={styles.title}>{article.title}</h1>
        <p className={styles.abstract}>{article.abstract}</p>

        <div className={styles.meta}>
          {article.author && (
            <span className={styles.byline}>
              <AuthorIcon className={styles.author_icon} />
              {article.author}
            </span>
          )}
          {article.date && (
            <span className={styles.date}>
              {article.date}{" "}
              {!isNaN(Date.parse(article.date)) && (
                <>(<ReactTimeAgo date={Date.parse(article.date)} locale="en-US" />)</>
              )}
            </span>
          )}
        </div>

        <hr className={styles.divider} />

        <figure
          className={styles.hero_figure}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        >
          <img
            className={styles.hero_img}
            src={`${getBase()}/content/images/${slug}.png`}
            alt={article.title}
            onError={(e) => { e.target.closest("figure").style.display = "none"; }}
          />
          {article.image_meta && (
            <figcaption className={styles.hero_caption}>{article.image_meta}</figcaption>
          )}
        </figure>

        <div className={`${styles.body} markdown-body`}>
          <ReactMarkdown>{body}</ReactMarkdown>
        </div>
      </article>
    </div>
  );
};
